import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Classify error types for better user experience
    const isPdfError = error.message.includes('GlobalWorkerOptions') ||
                      error.message.includes('PDF') ||
                      error.message.includes('pdfjs');
    const isAuthError = error.message.includes('token') ||
                       error.message.includes('auth') ||
                       error.message.includes('MSAL');
    const isNetworkError = error.message.includes('fetch') ||
                          error.message.includes('network') ||
                          error.message.includes('connection');

    console.error('Component Error:', {
      type: isPdfError ? 'PDF_ERROR' : isAuthError ? 'AUTH_ERROR' : isNetworkError ? 'NETWORK_ERROR' : 'GENERAL_ERROR',
      name: error.name,
      message: error.message.substring(0, 200)
    });

    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Enhanced error logging with better classification
    const isPdfError = error.message.includes('GlobalWorkerOptions') ||
                      error.message.includes('PDF') ||
                      error.message.includes('pdfjs');
    const isAuthError = error.message.includes('token') ||
                       error.message.includes('auth') ||
                       error.message.includes('MSAL');
    const isNetworkError = error.message.includes('fetch') ||
                          error.message.includes('network') ||
                          error.message.includes('connection');

    console.error('Error Boundary caught an error:', {
      type: isPdfError ? 'PDF_RENDERING_ERROR' : isAuthError ? 'AUTH_ERROR' : isNetworkError ? 'NETWORK_ERROR' : 'COMPONENT_ERROR',
      name: error.name,
      message: error.message.includes('token') ? 'Authentication error' : error.message.substring(0, 100),
      component: errorInfo.componentStack?.split('\n')[1]?.trim(),
      stack: process.env.NODE_ENV === 'development' ? error.stack : undefined
    });
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const isPdfError = error?.message.includes('GlobalWorkerOptions') ||
                        error?.message.includes('PDF') ||
                        error?.message.includes('pdfjs');
      const isAuthError = error?.message.includes('token') ||
                         error?.message.includes('auth') ||
                         error?.message.includes('MSAL');
      const isNetworkError = error?.message.includes('fetch') ||
                            error?.message.includes('network') ||
                            error?.message.includes('connection');

      const getErrorTitle = () => {
        if (isPdfError) return 'Document Rendering Error';
        if (isAuthError) return 'Authentication Error';
        if (isNetworkError) return 'Connection Error';
        return 'Application Error';
      };

      const getErrorMessage = () => {
        if (isPdfError) return 'There was an issue loading the document viewer. Please refresh the page and try again.';
        if (isAuthError) return 'There was an issue with the authentication process. Please refresh the page and try again.';
        if (isNetworkError) return 'There was a problem connecting to the service. Please check your internet connection and try again.';
        return 'An unexpected error occurred. Please refresh the page and try again.';
      };

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--dark-primary, #0f0f23)',
          color: 'var(--text-primary, #ffffff)',
          fontFamily: '"Inter", sans-serif'
        }}>
          <div style={{
            backgroundColor: 'var(--dark-secondary, #1a1a2e)',
            padding: '2rem',
            borderRadius: '1rem',
            textAlign: 'center',
            maxWidth: '400px',
            border: '1px solid var(--border-color, #ef4444)'
          }}>
            <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>
              {getErrorTitle()}
            </h2>
            <p style={{ color: 'var(--text-secondary, #a1a1aa)', marginBottom: '1.5rem' }}>
              {getErrorMessage()}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
